import type { GuideSourceDetails } from "@expert/solve-tools/src/guides/types";

import { isGuideSupported } from "@expert/solve-tools";

import type { usePartner } from "@/sdk";

import { isDeviceActivationIntent, isDeviceActivationQueue, isDeviceActivationSupportNumber } from "./isActivation";

/** Guide titles as the Logic Builder flow system lists them in `availableGuides`. */
export const DEVICE_ACTIVATION_GUIDE = "Device Activation";
const VERIZON_API_DEVICE_ACTIVATION_GUIDE = "Device Activation With API";

/**
 * The activation guide to auto-open. A Verizon expert with `isVerizonDeviceActivationApiEnabled` gets the API guide
 * when their flow system lists it, and the manual guide otherwise, so a flow system without the API guide still
 * auto-opens the one it has. Shared by every auto-open path (intent / queue / TFN and call-category recommendation).
 */
export function getActivationGuideTitle({
    isVerizonDeviceActivationApiEnabled,
    partner,
    sessionId,
}: {
    isVerizonDeviceActivationApiEnabled: boolean;
    partner: ReturnType<typeof usePartner>;
    sessionId: string;
}) {
    if (
        partner === "verizon" &&
        isVerizonDeviceActivationApiEnabled &&
        isGuideSupported(sessionId, VERIZON_API_DEVICE_ACTIVATION_GUIDE)
    )
        return VERIZON_API_DEVICE_ACTIVATION_GUIDE;
    return DEVICE_ACTIVATION_GUIDE;
}

interface OpenActivationGuideProps {
    disableAutomaticOpen: boolean;
    intent?: string;
    isCustomerSession: boolean;
    isCustomerVerificationComplete: boolean;
    isDeviceActivationGuideIntentAutoOpenEnabled: boolean;
    isGuideOpen: boolean;
    isLoadingSimplrSession: boolean;
    isVerizonDeviceActivationApiEnabled: boolean;
    partner: ReturnType<typeof usePartner>;
    requireVerification: boolean;
    sessionId: string;
    supportNumberCalled?: string;
    queueName?: string;
}
export function shouldOpenActivationGuide({
    disableAutomaticOpen,
    intent,
    isCustomerSession,
    isCustomerVerificationComplete,
    isDeviceActivationGuideIntentAutoOpenEnabled,
    isGuideOpen,
    isLoadingSimplrSession,
    isVerizonDeviceActivationApiEnabled,
    partner,
    requireVerification,
    sessionId,
    supportNumberCalled,
    queueName,
}: Readonly<OpenActivationGuideProps>): {
    shouldOpenGuide: boolean;
    sourceDetails?: GuideSourceDetails;
    guideTitle?: string;
} {
    const guideTitle = getActivationGuideTitle({ isVerizonDeviceActivationApiEnabled, partner, sessionId });
    if (
        !isGuideSupported(sessionId, guideTitle) ||
        isGuideOpen ||
        !isCustomerSession ||
        disableAutomaticOpen ||
        isLoadingSimplrSession
    )
        return { shouldOpenGuide: false };

    // Redesign (V2) holds auto-open until the customer is verified, so we don't surface a guided
    // flow before the expert has verified them. Legacy is intentionally exempt (requireVerification=false).
    if (requireVerification && !isCustomerVerificationComplete) return { shouldOpenGuide: false };

    if (isDeviceActivationIntent(intent) && isDeviceActivationGuideIntentAutoOpenEnabled)
        return { shouldOpenGuide: true, sourceDetails: "intent", guideTitle };
    if (isDeviceActivationQueue({ partner, queueName }))
        return { shouldOpenGuide: true, sourceDetails: "queue", guideTitle };
    if (isDeviceActivationSupportNumber({ partner, supportNumberCalled }))
        return { shouldOpenGuide: true, sourceDetails: "tfn", guideTitle };

    return { shouldOpenGuide: false };
}
